import React, { useEffect, useState } from "react";
import { Spinner, Button, Modal } from "@themesberg/react-bootstrap";
import { ToastAlert } from "../../components/ToastAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import API from "../../utils/api";
import { useTranslation } from "react-i18next";

const AdminFeedback = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const [alert, setAlert] = useState({ show: false, message: "", variant: "info" });
  const showAlert = (message, variant = "info") => setAlert({ show: true, message, variant });

  // Fetch all feedbacks
  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await API.get("/user-feedback");
      setReviews(res.data);
    } catch (err) {
      showAlert(t("alerts.fetchError"), "danger");
    }
    setLoading(false);
  };

  useEffect(() => { fetchReviews(); }, []);

  // Open delete modal
  const openDeleteModal = (review) => {
    setSelectedReview(review);
    setDeleteError("");
    setDeleteSuccess(false);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDeleteUser = async () => {
    if (!selectedReview) return;
    setDeleteLoading(true);
    try {
      await API.delete(`/delete-feedback/${selectedReview._id}`);
      setReviews(reviews.filter((r) => r._id !== selectedReview._id));
      setDeleteSuccess(true);
    } catch (err) {
      setDeleteError(t("alerts.deleteError"));
    }
    setDeleteLoading(false);
  };

  if (loading) return <div className="d-flex justify-content-center mt-5"><Spinner animation="border" /></div>;

  return (
    <div style={{ maxWidth: "800px", margin: "50px auto", padding: "25px", borderRadius: "14px", backgroundColor: "#ffffff", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
      <ToastAlert show={alert.show} onClose={() => setAlert({ ...alert, show: false })} message={alert.message} variant={alert.variant} />

      <h4 style={{ textAlign: "center", marginBottom: "25px", fontWeight: "700", color: "#1f2937" }}>
        {t("feedback1.title")}
      </h4>

      {reviews.length === 0 ? (
        <p style={{ textAlign: "center", color: "#6b7280" }}>{t("feedback1.noReviews")}</p>
      ) : (
        reviews.map((r) => (
          <div key={r._id} style={{ border: "1px solid #e5e7eb", padding: "20px", borderRadius: "12px", marginBottom: "15px", backgroundColor: "#fefefe", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", position: "relative", transition: "transform 0.1s" }} onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.01)")} onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}>
            {/* Delete Button */}
            <Button variant="outline-danger" size="sm" style={{ position: "absolute", top: "15px", right: "15px" }} onClick={() => openDeleteModal(r)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>

            {/* User Name */}
            <p style={{ marginBottom: "5px", fontWeight: "700", fontSize: "16px", color: "#111827" }}>
              {r.userName || r.companyName || t("feedback1.unknownUser")}
            </p>

            {/* Review Text */}
            <p style={{ marginBottom: "10px", fontSize: "14px", color: "#374151", lineHeight: "1.6" }}>{r.reviewText}</p>

            {/* Submission Time */}
            <p style={{ fontSize: "12px", color: "#6b7280" }}>
              {t("feedback1.submittedOn")} {new Date(r.createdAt).toLocaleString("en-US", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
        ))
      )}

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("feedback1.deleteModal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {deleteSuccess ? (
            <h5 className="text-success">{t("feedback1.deleteModal.success")}</h5>
          ) : (
            <>
              <p className="text-muted">{t("feedback1.deleteModal.confirm")}</p>
              {deleteError && <p className="text-danger mt-2">{deleteError}</p>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          {deleteSuccess ? (
            <Button variant="success" onClick={() => setShowDeleteModal(false)}>{t("feedback1.deleteModal.close")}</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>{t("feedback1.deleteModal.cancel")}</Button>
              <Button variant="danger" onClick={confirmDeleteUser} disabled={deleteLoading}>
                {deleteLoading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : t("feedback1.deleteModal.delete")}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminFeedback;
